import * as React from 'react';
import { TextField, InputAdornment, type TextFieldProps } from '@mui/material';
import { MoneyTextFieldProps } from './types';
import { showWarningSnackbar } from '../../../snackBar/snakbar';

const sanitize = (raw: string) => {
  let s = raw.replace(/[^\d.\-]/g, '');
  if (s.includes('-')) s = (s.startsWith('-') ? '-' : '') + s.replace(/-/g, '');
  const i = s.indexOf('.');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
};

const limit2dp = (s: string) => {
  const [int, dec] = s.split('.');
  if (dec === undefined) return int;
  return `${int}.${dec.slice(0, 2)}`;
};

const toNumberOrNull = (s: string): number | null => {
  if (s === '' || s === '-' || s === '.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const groupForTyping = (raw: string, locale: string): string => {
  if (raw === '' || raw === '-' || raw === '.') return raw;
  const neg = raw.startsWith('-');
  const body = neg ? raw.slice(1) : raw;
  const [int, dec] = body.split('.');

  const groupedInt =
    int === ''
      ? ''
      : new Intl.NumberFormat('en-IN', {
          useGrouping: true,
          maximumFractionDigits: 0,
        }).format(Number(int));

  let out = (neg ? '-' : '') + groupedInt;
  if (body.endsWith('.') && dec === undefined) out += '.';
  else if (dec !== undefined) out += '.' + dec;
  return out;
};

const formatFixed2 = (n: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(n);

const countDigits = (s: string, end: number) => (s.slice(0, end).match(/\d/g) || []).length;

const indexFromDigitCount = (s: string, k: number) => {
  if (k <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < s.length; i++) {
    if (/\d/.test(s[i])) seen++;
    if (seen >= k) return i + 1;
  }
  return s.length;
};

export const MoneyTextField: React.FC<MoneyTextFieldProps> = ({
  value: valueProp,
  defaultValue = null,
  currencySymbol = '',
  locale = 'en-IN',
  InputProps,
  inputProps,
  onBlur,
  onFocus,
  onChange,
  min = null,
  max = null,
  minErrorMessage,
  maxErrorMessage,
  ...rest
}) => {
  const isControlled = valueProp !== undefined;

  const [internalValue, setInternalValue] = React.useState<number | null>(defaultValue);

  const numericValue = isControlled ? valueProp ?? null : internalValue;

  const [editing, setEditing] = React.useState<string>('');
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const lastValidValueRef = React.useRef<number | null>(numericValue);
  const lastValidEditingRef = React.useRef<string>('');

  const display = focused ? editing : numericValue == null ? '0.00' : formatFixed2(numericValue, locale);

  React.useEffect(() => {
    if (!focused) {
      if (numericValue == null) {
        setEditing('');
        lastValidEditingRef.current = '';
      } else {
        const formatted = groupForTyping(String(numericValue), locale);
        setEditing(formatted);
        lastValidEditingRef.current = formatted;
      }
    }
    // Update lastValidValueRef when value changes externally (e.g., from props)
    if (numericValue !== null) {
      lastValidValueRef.current = numericValue;
    }
  }, [numericValue, focused, locale]);

  const validateValue = (n: number | null, showWarning = true): boolean => {
    if (n === null) return true;
    
    if (min !== null && min !== undefined && n < min) {
      if (showWarning) {
        const errorMessage = minErrorMessage || `The amount should be at least ${formatFixed2(min, locale)}`;
        showWarningSnackbar(errorMessage);
      }
      return false;
    }
    
    if (max !== null && max !== undefined && n > max) {
      if (showWarning) {
        const errorMessage = maxErrorMessage || `The amount should be below ${formatFixed2(max, locale)}`;
        showWarningSnackbar(errorMessage);
      }
      return false;
    }
    
    return true;
  };

  const setNumeric = (n: number | null, skipValidation = false) => {
    // During typing, we allow invalid values but show warnings (for performance)
    // On blur, we validate strictly and revert if invalid
    if (!skipValidation && n !== null) {
      validateValue(n, true); // Show warning but don't block
    }
    
    // Only update lastValidValueRef if the value is valid
    if (n !== null && validateValue(n, false)) {
      lastValidValueRef.current = n;
    }
    
    if (!isControlled) setInternalValue(n);
    onChange?.(n);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const incoming = el.value;
    const caret = el.selectionStart ?? incoming.length;

    const digitsBefore = countDigits(incoming, caret);

    let raw = incoming.replace(/,/g, '');
    
    // If min >= 0, prevent negative values by blocking minus sign early (optimized check)
    if (min !== null && min !== undefined && min >= 0 && raw.includes('-')) {
      showWarningSnackbar('Negative values are not allowed');
      // Revert to last valid editing state
      const lastValidEditing = lastValidEditingRef.current || (numericValue == null ? '' : groupForTyping(String(numericValue), locale));
      requestAnimationFrame(() => {
        const node = inputRef.current;
        if (!node) return;
        setEditing(lastValidEditing);
        const pos = Math.min(caret, lastValidEditing.length);
        node.setSelectionRange(pos, pos);
      });
      return;
    }
    
    raw = limit2dp(sanitize(raw));

    const grouped = groupForTyping(raw, locale);
    
    // Check if the value would be invalid before setting it
    const numValue = toNumberOrNull(raw);
    let shouldBlock = false;
    
    if (numValue !== null) {
      // Validate the value - if invalid, prevent typing
      const isValid = validateValue(numValue, true);
      if (!isValid) {
        // Block the invalid value - revert to last valid editing state
        shouldBlock = true;
        const lastValidEditing = lastValidEditingRef.current || (numericValue == null ? '' : groupForTyping(String(numericValue), locale));
        
        requestAnimationFrame(() => {
          const node = inputRef.current;
          if (!node) return;
          
          setEditing(lastValidEditing);
          // Restore cursor position as much as possible
          const pos = Math.min(caret, lastValidEditing.length);
          node.setSelectionRange(pos, pos);
        });
        return;
      } else {
        // Value is valid - update refs
        lastValidValueRef.current = numValue;
        lastValidEditingRef.current = grouped;
      }
    } else {
      // Allow null/incomplete values (empty, '-', '.') to be typed
      lastValidEditingRef.current = grouped;
    }
    
    setEditing(grouped);
    setNumeric(numValue, true);

    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;

      let pos: number;

      if (incoming.endsWith('.') && caret === incoming.length) {
        pos = grouped.length;
      } else if (incoming.startsWith('-') && caret === 1) {
        pos = 1;
      } else if (grouped === '.' || grouped === '-.') {
        pos = grouped.length;
      } else if (digitsBefore === 0 && grouped.includes('.') && (grouped.startsWith('.') || grouped.startsWith('-.'))) {
        pos = grouped.indexOf('.') + 1;
      } else {
        pos = indexFromDigitCount(grouped, digitsBefore);
      }

      const decimalIndexIncoming = incoming.indexOf('.');
      if (decimalIndexIncoming !== -1 && caret > decimalIndexIncoming) {
        const decimalIndexGrouped = grouped.indexOf('.');
        if (decimalIndexGrouped !== -1) {
          pos = Math.max(pos, decimalIndexGrouped + 1);
        }
      }

      node.setSelectionRange(pos, pos);
    });
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    // Initialize editing state from numericValue when focusing
    const initialEditing = numericValue == null 
      ? '' 
      : groupForTyping(String(numericValue), locale);
    setEditing(initialEditing);
    lastValidEditingRef.current = initialEditing;
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    const num = toNumberOrNull(editing.replace(/,/g, ''));
    if (num == null) {
      setEditing('');
      setNumeric(null, true);
    } else {
      const rounded = Number(num.toFixed(2));
      // Validate on blur and revert to last valid value if invalid
      if (!validateValue(rounded)) {
        const lastValid = lastValidValueRef.current;
        if (lastValid !== null) {
          setEditing(groupForTyping(String(lastValid), locale));
          setNumeric(lastValid, true);
        } else {
          setEditing('');
          setNumeric(null, true);
        }
      } else {
        setEditing(groupForTyping(String(rounded), locale));
        setNumeric(rounded, true);
      }
    }
    onBlur?.(e);
  };

  const mergedInputProps: TextFieldProps['InputProps'] = {
    ...(InputProps || {}),
    startAdornment:
      InputProps?.startAdornment ?? (currencySymbol ? <InputAdornment position="start">{currencySymbol}</InputAdornment> : undefined),
  };

  const mergedInnerInputProps: TextFieldProps['inputProps'] = {
    inputMode: 'decimal',
    ...inputProps,
    style: { textAlign: 'right', ...(inputProps?.style || {}) },
  };

  return (
    <TextField
      {...rest}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      InputProps={mergedInputProps}
      inputProps={mergedInnerInputProps}
      inputRef={inputRef}
      size={rest.size ?? 'small'}
      sx={{
        '.MuiInputBase-input': { textAlign: 'right' },
        ...(rest.sx || {}),
      }}
    />
  );
};
